import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ROUTES } from "@/config/routes";
import { isUiMockMode } from "@/config/features";
import { BillingGateModal } from "@/features/billing/components/BillingGateModal";
import { usePlanAccess } from "@/features/billing/hooks/usePlanAccess";
import type { BillingGateReason } from "@/types/api";
import type { DocumentListKind, GatedCapability } from "@/features/billing/hooks/usePlanAccess";
import { filterInvoicesByQuery } from "@/domain/invoices/filterInvoicesByQuery";
import { filterInvoicesByStatuses } from "@/domain/invoices/filterInvoicesByStatus";
import {
  needsClientStatusFilter,
  validateInvoiceFilterValues,
  serializeInvoiceFiltersToSearchParams,
} from "@/domain/invoices/invoiceFilterState";
import { isReceiptRowSelectable } from "@/domain/invoices/receiptRow";
import { sortInvoices } from "@/domain/invoices/sortInvoices";
import { DownloadListWorkspace } from "@/features/invoices/components/DownloadListWorkspace";
import { ExportModal } from "@/features/invoices/components/ExportModal";
import { InvoiceCursorPager } from "@/features/invoices/components/InvoiceCursorPager";
import { InvoiceFiltersPanel } from "@/features/invoices/components/InvoiceFiltersPanel";
import {
  InvoiceTable,
  type HostedLinkActions,
  type InvoiceTableVariant,
} from "@/features/invoices/components/InvoiceTable";
import { InvoiceTableToolbar } from "@/features/invoices/components/InvoiceTableToolbar";
import { StatChipInline } from "@/features/invoices/components/StatChipInline";
import { useCustomers } from "@/features/invoices/hooks/useCustomers";
import { useInvoiceFilters } from "@/features/invoices/hooks/useInvoiceFilters";
import { useInvoiceSearch } from "@/features/invoices/hooks/useInvoiceSearch";
import { useInvoiceSelection } from "@/features/invoices/hooks/useInvoiceSelection";
import { useVisitedPageItems } from "@/features/invoices/hooks/useVisitedPageItems";
import { PageHeader } from "@/features/layout/components/PageHeader";
import { downloadInvoicePdfDirect } from "@/lib/http/downloadDocument";
import { ApiErrorNotice } from "@/features/billing/components/ApiErrorNotice";
import type { InvoiceRow } from "@/types/api";
import { cn } from "@/utils/cn";
import { useFilterSearchButton } from "@/lib/query/useFilterSearchButton";

export type ExportUnitKind = "pdf" | "receipt";

export interface InvoicesWorkspaceProps {
  variant: InvoiceTableVariant;
  title: string;
  lead?: string;
  breadcrumbSuffix: string;
  tableTitle: string;
  tableHint?: string;
  defaultStatus?: string;
  exportKind?: ExportUnitKind;
  lockStatusToPaid?: boolean;
  /** Link to Bulk Export PDFs for large background ZIP jobs. */
  bulkExportCta?: boolean;
  /**
   * Compact table chrome for download list pages: hide subheading, inline stats.
   * Export stays in the fixed bottom bar.
   */
  compactDownloadLayout?: boolean;
  hostedLinkActions?: HostedLinkActions;
  fixedColumnLayout?: boolean;
}

const PAID_ONLY: string[] = ["paid"];
const NO_DEFAULT_STATUSES: string[] = [];

export function InvoicesWorkspace({
  variant,
  title,
  lead,
  breadcrumbSuffix,
  tableTitle,
  tableHint,
  defaultStatus = "",
  exportKind = "pdf",
  lockStatusToPaid,
  bulkExportCta = false,
  compactDownloadLayout = false,
  hostedLinkActions = "none",
  fixedColumnLayout = false,
}: InvoicesWorkspaceProps) {
  const navigate = useNavigate();
  const { withGate } = usePlanAccess();
  const [billingGate, setBillingGate] = useState<{
    reason: BillingGateReason;
    capability: GatedCapability;
  } | null>(null);

  const openGate = (reason: BillingGateReason, capability: GatedCapability) => {
    setBillingGate({ reason, capability });
  };
  const listKind: DocumentListKind = exportKind === "receipt" ? "receipt" : "invoice_pdf";

  const defaultStatuses = useMemo((): string[] => {
    if (lockStatusToPaid) return PAID_ONLY;
    if (defaultStatus) return [defaultStatus];
    return NO_DEFAULT_STATUSES;
  }, [lockStatusToPaid, defaultStatus]);

  const filters = useInvoiceFilters(defaultStatuses);
  const applied = lockStatusToPaid
    ? { ...filters.applied, statuses: PAID_ONLY }
    : filters.applied;

  const customersQuery = useCustomers();
  const customers = customersQuery.data?.items ?? [];

  const { data, isLoading, isFetching, isPlaceholderData, error } =
    useInvoiceSearch({
    appliedStatuses: applied.statuses,
    customerIds: applied.customerIds,
    pageSize: filters.pageSize,
    after: filters.after,
    dateFrom: applied.dateFrom,
    dateTo: applied.dateTo,
    amountMin: applied.amountMin,
    amountMax: applied.amountMax,
    dueDateFrom: applied.dueDateFrom,
    dueDateTo: applied.dueDateTo,
    });

  const mock = isUiMockMode();
  const rawItems = data?.items ?? [];
  const { searchLoading, markSearchStarted } = useFilterSearchButton(
    filters.appliedSearchParams.toString(),
    isLoading,
    isFetching,
    isPlaceholderData,
  );

  const filteredByStripe = useMemo(() => {
    let rows = rawItems;
    if (mock && needsClientStatusFilter(applied.statuses)) {
      rows = filterInvoicesByStatuses(rows, applied.statuses);
    }
    return sortInvoices(rows, "created-desc");
  }, [rawItems, mock, applied.statuses]);

  const items = useMemo(
    () => filterInvoicesByQuery(filteredByStripe, filters.tableSearch),
    [filteredByStripe, filters.tableSearch],
  );

  const isRowSelectable = useCallback(
    (row: InvoiceRow) =>
      exportKind === "receipt"
        ? isReceiptRowSelectable(row)
        : row.exportable !== false,
    [exportKind],
  );

  const selection = useInvoiceSelection(items, isRowSelectable);
  const [modalOpen, setModalOpen] = useState(false);
  const [allPagesSelected, setAllPagesSelected] = useState(false);

  const visitedResetKey = `${filters.appliedSearchParams}|${filters.pageSize}`;
  const { allVisited } = useVisitedPageItems(
    filteredByStripe,
    (row) => row.id,
    visitedResetKey,
    isRowSelectable,
  );

  const loadedSelectableIds = useMemo(() => {
    const rows = filters.tableSearch.trim()
      ? filterInvoicesByQuery(allVisited, filters.tableSearch)
      : allVisited;
    return rows.filter(isRowSelectable).map((r) => r.id);
  }, [allVisited, filters.tableSearch, isRowSelectable]);

  const pageSelectableCount = useMemo(
    () => items.filter(isRowSelectable).length,
    [items, isRowSelectable],
  );

  const clearSelection = () => {
    selection.clear();
    setAllPagesSelected(false);
  };

  const unitPlural = exportKind === "receipt" ? "receipts" : "PDFs";
  const modalTitle =
    exportKind === "receipt" ? "Export invoice receipts" : "Export invoice PDFs";
  const zipStatLabel =
    exportKind === "receipt" ? "Receipts in ZIP" : "PDFs in ZIP";

  const handleSearch = () => {
    const toApply = lockStatusToPaid
      ? { ...filters.draft, statuses: PAID_ONLY }
      : filters.draft;
    if (lockStatusToPaid) filters.setDraft(toApply);
    markSearchStarted(
      serializeInvoiceFiltersToSearchParams(
        toApply,
        new URLSearchParams(),
      ).toString(),
    );
    filters.commitSearch(toApply);
    clearSelection();
  };

  const selectAllLoadedPages = () => {
    if (loadedSelectableIds.length === 0) return;
    selection.setSelectedIds(loadedSelectableIds);
    setAllPagesSelected(true);
  };

  const [rowDownloadError, setRowDownloadError] = useState<unknown>(null);
  const [downloading, setDownloading] = useState<{
    invoiceId: string;
    kind: "pdf" | "receipt";
  } | null>(null);

  const onRowDownload = (row: InvoiceRow, kind: "pdf" | "receipt" = "pdf") => {
    if (mock) {
      selection.setSelectedIds([row.id]);
      setModalOpen(true);
      return;
    }
    const reason = withGate("inline_pdf", () => {
      setRowDownloadError(null);
      setDownloading({ invoiceId: row.id, kind });
      void downloadInvoicePdfDirect(
        row.id,
        kind === "receipt" ? "receipt" : "invoice_pdf",
        kind === "pdf"
          ? { pdfUrl: row.invoice_pdf_url, invoiceNumber: row.invoice_number }
          : undefined,
      )
        .catch((e) => setRowDownloadError(e))
        .finally(() => setDownloading(null));
    });
    if (reason) openGate(reason, "inline_pdf");
  };

  const appliedFilterInvalid =
    validateInvoiceFilterValues(applied).length > 0;
  const exportDisabled =
    filters.isDirty || appliedFilterInvalid || selection.selectedCount === 0;
  const allPagesSameAsThisPage =
    loadedSelectableIds.length > 0 &&
    loadedSelectableIds.length <= pageSelectableCount &&
    filters.page === 1;
  const allPagesTitle = allPagesSameAsThisPage
    ? "Open more pages with Next to include additional rows"
    : "All rows from pages you've opened this search";

  const handleToggleRow = (id: string) => {
    setAllPagesSelected(false);
    selection.toggle(id);
  };

  const handleToggleAllOnPage = () => {
    selection.toggleAllOnPage();
    setAllPagesSelected(false);
  };

  const exportZipButton = (
    <button
      type="button"
      className="btn-main btn-export-zip shrink-0 text-sm"
      disabled={exportDisabled}
      onClick={() => {
        const reason = withGate(
          "selection_export",
          () => setModalOpen(true),
          listKind,
        );
        if (reason) openGate(reason, "selection_export");
      }}
    >
      Export {selection.selectedCount} {unitPlural} as ZIP
    </button>
  );

  return (
    <>
      <DownloadListWorkspace
        pinned={
          <>
            <PageHeader
              title={title}
              lead={lead}
              breadcrumbSuffix={breadcrumbSuffix}
              className={compactDownloadLayout ? "page-head-row--compact" : undefined}
            />

            {bulkExportCta ? (
              compactDownloadLayout ? (
                <p className="bulk-export-cta-lite mb-2 text-sm leading-snug text-[var(--muted)]">
                  <strong className="font-semibold text-[var(--text)]">
                    Many invoices?
                  </strong>{" "}
                  Use a date range for a background ZIP export.{" "}
                  <Link
                    to={ROUTES.invoicesBulkExport}
                    className="link-quiet font-semibold"
                  >
                    Bulk Export PDFs →
                  </Link>
                </p>
              ) : (
                <div className="bulk-export-cta mb-4 flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <p className="m-0 text-sm text-[var(--text)]">
                    <strong className="font-semibold">Many invoices?</strong>{" "}
                    Set a date range and run a background ZIP export.
                  </p>
                  <Link
                    to={ROUTES.invoicesBulkExport}
                    className="btn-main shrink-0 text-sm"
                  >
                    Bulk Export PDFs
                  </Link>
                </div>
              )
            ) : null}

            {rowDownloadError ? (
              <ApiErrorNotice
                error={rowDownloadError}
                context="download"
                className="mx-0 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
              />
            ) : null}

            <InvoiceFiltersPanel
              draft={filters.draft}
              applied={applied}
              isDirty={filters.isDirty}
              customers={customers}
              customersLoading={customersQuery.isLoading}
              customersError={null}
              onDraftChange={filters.setDraft}
              onSearch={handleSearch}
              searchLoading={searchLoading}
              onReloadCustomers={() => void customersQuery.refetch()}
              hideStatus={lockStatusToPaid}
              statusHint={
                lockStatusToPaid ? "Paid invoices only." : undefined
              }
              dateFilterHint={undefined}
              compact={compactDownloadLayout}
            />

            {!compactDownloadLayout ? (
              <div className="list-block-head mb-3">
                <h2>{tableTitle}</h2>
                {tableHint ? <p>{tableHint}</p> : null}
              </div>
            ) : null}

            <div
              className={cn(
                "table-meta-band",
                compactDownloadLayout && "table-meta-band--compact",
              )}
            >
              {compactDownloadLayout ? (
                <div className="selection-stats-inline">
                  <StatChipInline
                    n={filteredByStripe.length}
                    label="on page"
                  />
                  <StatChipInline
                    n={selection.selectedCount}
                    label="selected"
                  />
                </div>
              ) : (
                <div
                  className={cn(
                    "selection-stats border-b-0",
                    "grid-cols-2 sm:grid-cols-3",
                  )}
                >
                  <StatMini n={filteredByStripe.length} l="This page" />
                  <StatMini n={selection.selectedCount} l="Selected" />
                  <StatMini n={selection.selectedCount} l={zipStatLabel} />
                </div>
              )}

              <InvoiceTableToolbar
                pageSize={filters.pageSize}
                search={filters.tableSearch}
                onPageSizeChange={(size) => {
                  filters.setPageSize(size);
                  clearSelection();
                }}
                onSearchChange={filters.setTableSearch}
                selection={{
                  pageSelectableCount,
                  allOnPageSelected: selection.allOnPageSelected,
                  someOnPageSelected: selection.someOnPageSelected,
                  onTogglePage: handleToggleAllOnPage,
                  onSelectAllPages: selectAllLoadedPages,
                  allPagesDisabled: allPagesSameAsThisPage || isLoading,
                  allPagesActive: allPagesSelected,
                  loadedPagesSelectableCount: loadedSelectableIds.length,
                  totalSelectedCount: selection.selectedCount,
                  onClearSelection: clearSelection,
                  allPagesTitle,
                }}
              />
            </div>
          </>
        }
        table={
          <div className="panel invoices-workspace">
        {isLoading && rawItems.length === 0 ? (
          <p className="px-4 py-6 text-sm text-[var(--muted)]">Refreshing…</p>
        ) : null}
        {!error && rawItems.length > 0 ? (
          <InvoiceTable
            variant={variant}
            items={items}
            customers={customers}
            selected={selection.selected}
            onToggle={handleToggleRow}
            onRowDownload={onRowDownload}
            downloading={downloading}
            hostedLinkActions={hostedLinkActions}
            fixedColumnLayout={compactDownloadLayout || fixedColumnLayout}
            embedded
          />
        ) : null}
        {!isLoading && !isFetching && !error && rawItems.length === 0 ? (
          <p className="empty-state px-4 py-8 text-sm text-[var(--muted)]">
            No invoices match these filters.
          </p>
        ) : null}
          </div>
        }
        pager={
          <InvoiceCursorPager
            compact
            itemCount={rawItems.length}
            page={filters.page}
            canGoPrev={filters.canGoPrev}
            hasMore={data?.has_more ?? false}
            pageLoading={isFetching && isPlaceholderData}
            onPrev={filters.goPrev}
            onNext={() => {
              const reason = withGate(
                "paginate",
                () => {
                  if (data?.next_cursor) filters.goNext(data.next_cursor);
                },
                listKind,
              );
              if (reason) openGate(reason, "paginate");
            }}
            detail={
              filters.tableSearch && items.length !== rawItems.length
                ? `(${items.length} after find in table)`
                : filters.tableSearch
                  ? "(find in table active)"
                  : undefined
            }
          />
        }
        footer={
          <div
            className={cn(
              "export-sticky",
              compactDownloadLayout && "export-sticky--compact",
              filters.isDirty && "is-pending-search",
            )}
          >
            {exportZipButton}
            {filters.isDirty ? (
              <span className="text-sm font-semibold text-[var(--warn)]">
                Search to apply filter changes before selecting or exporting.
              </span>
            ) : null}
          </div>
        }
      />

      <ExportModal
        open={modalOpen}
        invoiceIds={selection.selectedIds}
        documentType={exportKind === "receipt" ? "receipt" : "invoice_pdf"}
        title={modalTitle}
        onClose={() => setModalOpen(false)}
        onStarted={(jobId) => navigate(ROUTES.exportDetail(jobId))}
      />

      <BillingGateModal
        open={billingGate != null}
        reason={billingGate?.reason ?? null}
        capability={billingGate?.capability}
        onClose={() => setBillingGate(null)}
      />
    </>
  );
}

function StatMini({ n, l }: { n: number; l: string }) {
  return (
    <div className="stat-mini">
      <div className="n">{n}</div>
      <div className="l">{l}</div>
    </div>
  );
}
